/**
 * One load of what the customer Settings page shows: credit control's composed
 * read (`/views/customer-credit`: payment methods, stop, holds, recent
 * changes, the write links, and the sales ledger's invoicing settings). A
 * change (a save, a stop, a lift) reads it again and tells the header chip, so
 * the two never disagree.
 */
import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CustomerCreditView } from "@shared-core/data/credit-control-types";
import { notifyCreditChanged } from "@shared-core/domain/creditEvents";

export type SettingsLoad<T> =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; value: T };

const failure = (err: unknown, fallback: string): SettingsLoad<never> => ({
  kind: "failed",
  message: err instanceof Error && err.message ? err.message : fallback,
});

export function useCustomerSettings(customerId: string) {
  const adapter = useDataAdapter();
  const [credit, setCredit] = useState<SettingsLoad<CustomerCreditView>>({
    kind: "loading",
  });

  useEffect(() => {
    let cancelled = false;
    setCredit({ kind: "loading" });
    adapter
      .getCustomerCredit(customerId)
      .then((value) => {
        if (!cancelled) setCredit({ kind: "loaded", value });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setCredit(failure(err, "Could not load payment settings."));
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, customerId]);

  /** A credit change was saved: read it again, and tell the header chip. */
  const reloadCredit = useCallback(async () => {
    try {
      const value = await adapter.getCustomerCredit(customerId);
      setCredit({ kind: "loaded", value });
      notifyCreditChanged();
    } catch (err) {
      setCredit(failure(err, "Could not load payment settings."));
    }
  }, [adapter, customerId]);

  return { credit, reloadCredit };
}
